import { DataTexture, LinearFilter, ClampToEdgeWrapping, RGBAFormat, Vector3, Vector4 } from "three";
import { PLAN_SIDE, PLAN_SPACING, WORLD_HALF, WORLD_SIZE, type PlanData } from "@eldrun/worldgen";

/**
 * A arrebentação (Fase 15): parâmetros, a textura da costa e o GLSL do trecho `surf` (`chunks.ts`), com
 * o espelho em TS das curvas (testes).
 */

/** Distância máxima guardada na textura da costa (m; Uint8 a 1 m por passo). */
export const SHORE_MAX = 255;

/**
 * Comprimento entre cristas (m), período (s: a frente anda ~1,8 m/s), altura da onda ao quebrar (m) e
 * subida máxima do espraiado na areia (m acima do nível do mar; nas praias de ~1:12 do worldgen, uns 4 m
 * de areia coberta e descoberta a cada onda).
 */
export const SURF_PARAMS = [11, 6, 0.34, 0.24] as const;

/**
 * A distância à costa com sinal pelo plano (8 m): R = metros até a terra (no mar), G = até o mar (na
 * terra), limitados a `SHORE_MAX`. O shader faz R − G: positivo no mar, negativo na terra, zero na praia.
 * B e A: o gradiente dessa distância (x e z, de −1 a 1 em 0–255), calculado aqui uma vez — antes o
 * vértice do mar lia a textura mais duas vezes para a inclinação da arrebentação.
 */
export function createShoreTexture(plan: Pick<PlanData, "landDistance" | "coastDistance">): DataTexture {
  const n = PLAN_SIDE;
  const signed = (i: number, j: number) => {
    const k = Math.min(n - 1, Math.max(0, j)) * n + Math.min(n - 1, Math.max(0, i));
    return Math.min(plan.landDistance[k] as number, SHORE_MAX) - Math.min(plan.coastDistance[k] as number, SHORE_MAX);
  };
  const encode = (g: number) => Math.round((Math.min(1, Math.max(-1, g)) * 0.5 + 0.5) * 255);
  const data = new Uint8Array(n * n * 4);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = j * n + i;
      data[k * 4] = Math.round(Math.min(plan.landDistance[k] as number, SHORE_MAX));
      data[k * 4 + 1] = Math.round(Math.min(plan.coastDistance[k] as number, SHORE_MAX));
      data[k * 4 + 2] = encode((signed(i + 1, j) - signed(i - 1, j)) / (2 * PLAN_SPACING));
      data[k * 4 + 3] = encode((signed(i, j + 1) - signed(i, j - 1)) / (2 * PLAN_SPACING));
    }
  }
  const texture = new DataTexture(data, n, n, RGBAFormat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/** Uniforms do trecho `surf` (a textura vem de `createShoreTexture`). */
export function surfUniforms(shore: DataTexture) {
  return {
    uShore: { value: shore },
    uShoreGrid: { value: new Vector3(PLAN_SIDE, -WORLD_HALF, WORLD_SIZE) },
    uSurf: { value: new Vector4(...SURF_PARAMS) },
  };
}

/** Fração do ciclo em que o espraiado chega ao alto (sobe rápido, desce devagar). */
export const SWASH_PEAK = 0.28;

/** Perfil da onda no ciclo `u` (crista em 0): costas longas para o mar, frente íngreme para a praia. */
export function surfProfile(u: number): number {
  if (u < 0.9) return (1 - u / 0.9) ** 2.5;
  const t = (u - 0.9) / 0.1;
  return t * t * (3 - 2 * t);
}

/** Média de `surfProfile` num ciclo: a onda sobe e desce em volta do nível do mar. */
export const SURF_PROFILE_MEAN = 0.9 / 3.5 + 0.05;

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Altura do espraiado (fração da subida máxima) no ciclo `u` da onda que chegou à praia. */
export function surfSwash(u: number): number {
  return smoothstep(0, SWASH_PEAK, u) * (1 - smoothstep(SWASH_PEAK, 0.95, u));
}

/** Até onde a areia está molhada (fração da subida máxima): o espraiado, ou a marca da última onda secando até a metade. */
export function surfWetLine(u: number): number {
  const dry = u < SWASH_PEAK ? 0.5 : 0.5 + 0.5 * (1 - smoothstep(SWASH_PEAK, 1, u));
  return Math.max(surfSwash(u), dry);
}

/** O GLSL do trecho `surf` (o mesmo nos dois estágios); espelha as funções acima. */
export const SURF = /* glsl */ `
uniform sampler2D uShore;
// Lado do plano, canto e tamanho do mundo (a textura cobre o mapa; fora dele, mar aberto).
uniform vec3 uShoreGrid;
// x: comprimento entre cristas (m), y: período (s), z: altura ao quebrar (m), w: subida na areia (m).
uniform vec4 uSurf;
// Distância à costa com sinal (m; x: positiva no mar, negativa na terra) e o gradiente dela (yz), numa leitura.
vec3 surfShoreField(vec2 xz) {
  vec2 uv = (xz - uShoreGrid.y) / uShoreGrid.z;
  if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec3(${SHORE_MAX.toFixed(1)}, 0.0, 0.0);
  vec4 d = textureLod(uShore, (uv * (uShoreGrid.x - 1.0) + 0.5) / uShoreGrid.x, 0.0);
  return vec3((d.r - d.g) * ${SHORE_MAX.toFixed(1)}, d.ba * 2.0 - 1.0);
}
float surfShore(vec2 xz) {
  return surfShoreField(xz).x;
}
// Séries de ondas: a força vem e vai devagar ao longo da costa (0,45 a 1).
float surfSet(vec2 xz) {
  return 0.45 + 0.55 * noise(xz * 0.018 + vec2(uTime * 0.021, -uTime * 0.013));
}
// Fase da onda: a crista (fração 0) anda para a praia; o ruído entorta a frente devagar.
float surfPhase(vec2 xz, float shore) {
  return max(shore, 0.0) / uSurf.x + uTime / uSurf.y + noise(xz * 0.012) * 1.6;
}
float surfProfile(float u) {
  return u < 0.9 ? pow(1.0 - u / 0.9, 2.5) : smoothstep(0.9, 1.0, u);
}
// Derivada de surfProfile em u.
float surfProfileSlope(float u) {
  if (u < 0.9) return -2.7778 * pow(1.0 - u / 0.9, 1.5);
  float k = (u - 0.9) * 10.0;
  return 60.0 * k * (1.0 - k);
}
float surfSwash(float u) {
  return smoothstep(0.0, ${SWASH_PEAK.toFixed(2)}, u) * (1.0 - smoothstep(${SWASH_PEAK.toFixed(2)}, 0.95, u));
}
// Altura (m acima do mar) a que o espraiado chega agora, com a força da série (\`surfSet\`) já calculada.
float surfRunup(vec2 xz, float set) {
  return uSurf.w * set * surfSwash(fract(surfPhase(xz, 0.0)));
}
// Até que altura a areia está molhada: o espraiado, ou a marca da última onda secando até a metade.
float surfWetLine(vec2 xz) {
  float u = fract(surfPhase(xz, 0.0));
  float dry = u < ${SWASH_PEAK.toFixed(2)} ? 0.5 : 0.5 + 0.5 * (1.0 - smoothstep(${SWASH_PEAK.toFixed(2)}, 1.0, u));
  return uSurf.w * surfSet(xz) * max(surfSwash(u), dry);
}
`;
